/**
 * Normalizes an untrusted "go back here afterwards" value (`?back_to=`, `?exit_to=`) into a path
 * this app can link to, or `undefined` when the value points anywhere else.
 *
 * These values reach us from the URL, so any visitor can set them. Left unchecked they are an open
 * redirect: a link to our own domain that bounces the visitor to an attacker's page, wearing our
 * origin's trust on the way out. Every site that turns one into a destination has to run it through
 * here first.
 *
 * Callers may send either a path (`/dasbor?tab=x`) or a whole same-origin URL — a cross-app caller
 * building the link with `buildUrl('school', ...)` has no relative form to send. Both come back as
 * a router-usable path.
 *
 * Resolution is delegated to the URL parser rather than to string prefix checks: the parser is what
 * the browser would use for the link anyway, so the two cannot disagree about where a value points.
 * That also covers the shapes prefix checks miss — `/\evil.com`, an embedded tab, `javascript:`,
 * and `https://our-host@evil.com`.
 *
 * @param value - the untrusted value, typically straight off a search param
 * @param origin - origin to accept, defaulting to the browser's current one. Pass it explicitly
 *   outside a browser: with no location to read, every value is rejected.
 */
export function toInternalPath(value: string | null | undefined, origin?: string): string | undefined {
  if (!value) return undefined

  // Reached through `globalThis` rather than `window` so this package keeps compiling without the
  // DOM lib — it is imported by Node-side callers too.
  const browserOrigin = (globalThis as { location?: { origin?: string } }).location?.origin
  const currentOrigin = origin ?? browserOrigin
  if (!currentOrigin) return undefined

  let resolved: URL
  try {
    resolved = new URL(value, currentOrigin)
  } catch {
    return undefined
  }

  // A non-hierarchical scheme (`javascript:`, `data:`) parses with an opaque origin, so this also
  // covers the values that never had a host to compare in the first place.
  if (resolved.origin !== currentOrigin) return undefined

  return `${resolved.pathname}${resolved.search}${resolved.hash}`
}
